import React, { FC, Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { Button } from "react-bootstrap";
import { TFunction, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { Link, useHistory } from "react-router-dom";
import { apiOrganization } from "src/services/Api/Organization/apiOrganization";
import { wrapPromise } from "go-core";
import { ButtonLoading } from "go-form";
import useFlash, { handleErrorForAlert } from "go-alert/AlertMessage";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import handleError from "go-app/services/errors";
import { ListDataRequestResponse } from "go-core/api/types";
import EmptyData from "go-core/components/EmptyData";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { LoadingContainer } from "go-core/components/Loading";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { exportPdf } from "go-list/core/components/Actions/services";
import { ListConfigField } from "go-list/core/services/types";
import { ListData } from "go-list/list";
import { listDataFetch } from "go-list/list/services/fetch-data";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import {
	selectColumns,
	selectFilters,
	selectSelectedColumns,
	selectSelectedFilters,
	selectSelectedFiltersFullNames,
} from "go-list/list/services/selectors";
import { ListConfig, ListParamsType } from "go-list/list/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { OrganizationState } from "go-security/services/organizations/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext, SegmentListService } from "go-segment/context";
import {
	StocktakingApi,
	StocktakingDraftApi,
	StocktakingDraftStateApi,
} from "../../../../../../../../../../../../services/Api/Organization/types";
import { StocktakingContext, StocktakingMobileActionsContext } from "../../../../../Preview";
import { StocktakingDraftsContext } from "../StocktakingDraftsTab";
import AddProductModalForm from "./AddProductModalForm";
import AutofillDraftStateListModalForm from "./AutofillDraftStateListModalForm";
import DraftStateInput from "./DraftStateInput";

interface ListState {
	resource?: Record<string, any>;
}

interface MatchParams {
	draftId: string;
}

const listName = "STOCKTAKING_DRAFT_STATE";
const resourceType = "STOCKTAKING_DRAFT_STATE";

const buildConfig = (
	t: TFunction,
	data: Record<string, any>,
	segmentListService: SegmentListService,
	stocktaking: StocktakingApi,
	draftId: string,
	organization: OrganizationState,
	drafts: StocktakingDraftApi[],
	removeDraftState: (draftState: StocktakingDraftStateApi) => Promise<void>,
	onTableCellInputBlur: (updatedItem: StocktakingDraftStateApi) => void
) => {
	const draft = drafts.find(({ id }) => id === Number(draftId));
	const allowToEdit = stocktaking.stocktaking_status !== "CLOSED";

	let config = {
		fields: [
			{
				id: "category",
				name: t("common.word.category"),
				render: (item: StocktakingDraftStateApi) => item.product.category.name,
				exportColumnFixedWidth: 100,
			},
			{
				id: "product",
				name: t("common.word.product"),
				render: (item: StocktakingDraftStateApi) => (
					<Link to={`/${organization.id}/products/${item.product.id}/edit`} target="_blank" rel="noreferrer">
						{item.product.name}
					</Link>
				),
				renderExport: (item: StocktakingDraftStateApi) => item.product.name,
				exportColumnFixedWidth: 200,
			},
			{
				id: "sku",
				name: t("common.word.sku"),
				type: "text",
				render: (item: StocktakingDraftStateApi) => item.product?.sku,
				exportColumnFixedWidth: 100,
			},
			{
				id: "volume",
				name: t("common.word.volume"),
				render: (item: StocktakingDraftStateApi) =>
					allowToEdit ? (
						<DraftStateInput
							draftId={Number(draftId)}
							item={item}
							type="volume"
							key={`volume-${item.id}-${item.volume}`}
							stocktakingId={stocktaking.id}
							onTableCellInputBlur={onTableCellInputBlur}
						/>
					) : (
						item.volume
					),
				renderExport: (item: StocktakingDraftStateApi) => {
					const measureType = t(`enums.products.measure_types.${item.product.measure_type}`);
					return `${item.volume} ${measureType}`;
				},
				exportColumnFixedWidth: 200,
			},
			{
				id: "amount",
				name: t("modules.stocktaking_draft.field.amount.title"),
				render: (item: StocktakingDraftStateApi) =>
					allowToEdit ? (
						<DraftStateInput
							key={`amount-${item.id}-${item.amount}`}
							draftId={Number(draftId)}
							item={item}
							type="amount"
							stocktakingId={stocktaking.id}
							onTableCellInputBlur={onTableCellInputBlur}
						/>
					) : (
						item.amount
					),
				renderExport: (item: StocktakingDraftStateApi) => item.amount,
				exportColumnFixedWidth: 200,
			},
		],
		actions: [
			{
				name: t("lib:common.action.remove"),
				click: (item: StocktakingDraftStateApi) => removeDraftState(item),
				visible: (_: StocktakingDraftStateApi) => stocktaking.stocktaking_status !== "CLOSED",
				refreshListAfter: true,
			},
		],
		filters: [
			{
				id: "productIds",
				name: t("common.word.product"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getProductSearchSelect(search, params, { cancelToken: options?.token }),
				},
			},
			{
				id: "categoryIds",
				name: t("common.word.category"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getProductCategorySearchSelect(search, params, { cancelToken: options?.token }),
				},
			},
		],
		selectedColumns: ["product", "volume", "amount"],
		customPageSizes: ["20", "50", "100", "1000"],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		exportConfig: {
			title: `${t("modules.stocktaking_draft.field.export_config.title", { name: draft?.name })} ${
				stocktaking.document_number
			}`,
			filename: `${draft?.name}_${FormatDate(stocktaking.stocktaking_at).replaceAll(".", "-")}`,
			everyOtherLineInDifferentColor: true,
			organization: organization.name,
		},
		fetch: (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "category,product";
			return apiOrganization.getStocktakingDraftStates(stocktaking.id, Number(draftId), params, {
				cancelToken: sourceToken?.token,
			});
		},
		saveSegment: (segment: SegmentType) => segmentListService.save(listName, resourceType, segment),
	} as ListConfig;

	const valueField = {
		id: "value",
		name: t("common.word.value"),
		render: (item: StocktakingDraftStateApi) =>
			allowToEdit ? (
				<DraftStateInput
					draftId={Number(draftId)}
					item={item}
					type="value"
					stocktakingId={stocktaking.id}
					key={`value-${item.id}-${item.value?.amount}`}
					onTableCellInputBlur={onTableCellInputBlur}
				/>
			) : (
				FormatMoney(item.value)
			),
		renderExport: (item: StocktakingDraftStateApi) => item.value?.amount,
	};

	if (stocktaking.type === "VALUE") {
		config.fields = [...(config.fields || []), valueField];
		config.selectedColumns = [...(config.selectedColumns || []), valueField.id];
	}

	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};
	return config;
};

const List: FC<ListState> = ({ resource }) => {
	const { t } = useTranslation();
	const [responseData, setResponseData] = useState<StocktakingDraftStateApi[]>([]);
	const segmentContextValue = useContext(SegmentContext);
	const { stocktaking } = useContext(StocktakingContext);
	const { drafts, setDrafts } = useContext(StocktakingDraftsContext);
	const mobileActions = useContext(StocktakingMobileActionsContext);
	const { draftId } = useParams<MatchParams>();
	const organization = useSelector(selectOrganization);
	const [showAddProductModal, setShowAddProductModal] = useState<boolean>(false);
	const [showAutofillDraftModal, setShowAutofillDraftModal] = useState<boolean>(false);
	const { addFlash, addSuccessFlash } = useFlash();
	const [loading, setLoading] = useState<boolean>(false);
	const confirmation = useConfirmation();
	const history = useHistory();
	const [forceFetchingList, setForceFetchingList] = useState(false);

	const onTableCellInputBlur = (updatedItem: StocktakingDraftStateApi) => {
		setResponseData((previousResponseData) => {
			const newPreviousResponseData = [...previousResponseData];
			const itemToBeUpdatedIndex = newPreviousResponseData.findIndex((item) => item.id === updatedItem.id);
			newPreviousResponseData.splice(itemToBeUpdatedIndex, 1, updatedItem);

			return newPreviousResponseData;
		});
	};

	if (!resource) return null;
	const data = resource.read();

	const removeDraftState = async (draftState: StocktakingDraftStateApi) => {
		await confirmation({
			title: t("lib:confirmation.title"),
			message: t("lib:confirmation.message.remove"),
		});
		await apiOrganization.removeStocktakingDraftState(stocktaking.id, Number(draftId), draftState.id);
		addSuccessFlash(t("lib:common.flash.removed"));
	};

	const refresh = async () => {
		setForceFetchingList(true);
	};

	const config = buildConfig(
		t,
		data,
		segmentContextValue,
		stocktaking,
		draftId,
		organization,
		drafts,
		removeDraftState,
		onTableCellInputBlur
	);

	const onRemove = async () => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("lib:confirmation.message.remove"),
			});
			setLoading(true);
			await apiOrganization.removeStocktakingDraft(stocktaking.id, Number(draftId));
			const newDrafts = [...drafts];
			const draftIndex = newDrafts.findIndex(({ id }) => id === Number(draftId));
			if (draftIndex > -1) newDrafts.splice(draftIndex, 1);
			setDrafts(newDrafts);
			addSuccessFlash(t("lib:common.flash.removed"));
			history.push(`/${organization.id}/stocktakings/${stocktaking.id}/drafts`);
			setLoading(false);
		} catch (e) {
			handleError.alert(e, addFlash);
		}
	};

	const actions = [
		{
			name: t("modules.stocktaking_draft.action.add_product.title"),
			click: () => setShowAddProductModal(true),
		},
		{
			name: t("modules.stocktaking_draft.action.autofill_draft.title"),
			click: () => setShowAutofillDraftModal(true),
		},
		{
			name: t("modules.stocktaking_draft.action.remove_draft.title"),
			click: onRemove,
			variant: "danger",
		},
	];

	const emptyList = <EmptyData actions={actions} description={t("lib:go_core.empty_list_data.description")} />;

	const customActions = (
		<>
			<Button onClick={() => setShowAddProductModal(true)}>
				{t("modules.stocktaking_draft.action.add_product.title")}
			</Button>
			<Button variant="light" onClick={() => setShowAutofillDraftModal(true)}>
				{t("modules.stocktaking_draft.action.autofill_draft.title")}
			</Button>
			<ButtonLoading loading={loading} onClick={onRemove} variant="danger">
				{t("modules.stocktaking_draft.action.remove_draft.title")}
			</ButtonLoading>
		</>
	);

	const exportToEmptyPdf = (state: ListParamsType) => {
		const newConfig = { ...config };

		const newState = { ...state };
		newState.page = "0";
		newState.size = "0";

		newConfig.fields = (newConfig?.fields || []).map((field: ListConfigField) => {
			switch (field.id) {
				case "amount":
					return {
						...field,
						exportColumnFixedWidth: 150,
						renderExport: () => "",
					};
				case "value":
					return {
						...field,
						renderExport: () => "",
					};
				default:
					return field;
			}
		});

		listDataFetch(newState, newConfig?.fetch)
			.then((res) => {
				const exportData = res as ListDataRequestResponse<StocktakingDraftStateApi, undefined>;
				const selectedColumns = selectSelectedColumns(state, newConfig);
				const columns = selectColumns(newConfig, selectedColumns);
				const selectedFilters = selectSelectedFilters(state, newConfig.filterValues);
				const filters = selectFilters(newConfig);
				const currentDraft = drafts.find(({ id }) => id?.toString() === draftId);
				const additionalInformationItemFontSize = newConfig.exportConfig?.pdfFontSize
					? Number(newConfig.exportConfig?.pdfFontSize) + 3
					: 12;
				const additionalInformation: { text: string; fontSize: number }[] = [
					{
						text: `${t("common.word.stock")}: ${stocktaking.stock.name}`,
						fontSize: additionalInformationItemFontSize,
					},
					{
						text: `${t("modules.stocktaking.field.stocktaking_at.title")}: ${FormatDate(
							stocktaking.stocktaking_at
						)}`,
						fontSize: additionalInformationItemFontSize,
					},
					{
						text: `${t("modules.stocktaking_draft.field.draft_type.title")}: ${t(
							`enums.stocktaking.drafts.label.${currentDraft?.type}`
						)}`,
						fontSize: additionalInformationItemFontSize,
					},
				];

				exportPdf(
					selectedColumns,
					columns,
					selectSelectedFiltersFullNames(selectedFilters, filters),
					exportData.data,
					newConfig?.exportConfig,
					additionalInformation,
					config.nested_config?.field
				);
			})
			.catch((err) => {
				handleErrorForAlert(err, addFlash);
			});
	};

	const customExportActions = [
		{
			title: t("modules.stocktaking_draft.action.generate_empty_pdf.title"),
			onClick: (state: ListParamsType) => exportToEmptyPdf(state),
		},
	];

	const newMobileActions: MobileActionProps[] = [
		...mobileActions,
		{
			title: t("modules.stocktaking_draft.action.add_product.title"),
			action: () => setShowAddProductModal(true),
		},
		{
			title: t("modules.stocktaking_draft.action.autofill_draft.title"),
			action: () => setShowAutofillDraftModal(true),
		},
		{
			title: t("modules.stocktaking_draft.action.remove_draft.title"),
			action: onRemove,
		},
	];

	return (
		<>
			<ListData
				data={responseData}
				config={config}
				onFetch={(items: StocktakingDraftStateApi[]) => setResponseData(items)}
				emptyList={emptyList}
				customActions={customActions}
				withoutSegments
				customExportActions={customExportActions}
				setForceFetchingList={setForceFetchingList}
				forceFetchingList={forceFetchingList}
				mobileActions={newMobileActions}
			/>
			{showAddProductModal && (
				<AddProductModalForm
					isShown={showAddProductModal}
					onHide={() => setShowAddProductModal(false)}
					stocktaking={stocktaking}
					draftId={Number(draftId)}
					handleSuccessAction={refresh}
				/>
			)}
			{showAutofillDraftModal && (
				<AutofillDraftStateListModalForm
					isShown={showAutofillDraftModal}
					stocktakingId={stocktaking.id}
					draftId={Number(draftId)}
					handleSuccessAction={refresh}
					onHide={() => setShowAutofillDraftModal(false)}
				/>
			)}
		</>
	);
};

const StocktakingDraftStateList = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = useContext(SegmentContext);
	const { draftId } = useParams<MatchParams>();

	useEffect(() => {
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<div className="stocktaking-draft-state-list">
			<Suspense fallback={<LoadingContainer />}>
				<List key={draftId} resource={resource} />
			</Suspense>
		</div>
	);
};

export default StocktakingDraftStateList;
